import { Check, MessageCircle, Package, ShoppingCart, User } from 'lucide-react'

export interface OrderWizardStepDef {
  step: 1 | 2 | 3
  title: string
  subtitle: string
}

interface OrderWizardStepperProps {
  steps: ReadonlyArray<OrderWizardStepDef>
  current: 1 | 2 | 3
  onSelect: (step: 1 | 2 | 3) => void
}

/** Stepper compacto con conectores y check en pasos completados. */
export function OrderWizardStepper({ steps, current, onSelect }: OrderWizardStepperProps) {
  return (
    <nav aria-label="Pasos de creación de orden" className="flex items-center gap-1 sm:gap-2">
      {steps.map((s, idx) => {
        const isActive = current === s.step
        const isDone = current > s.step
        return (
          <div key={s.step} className="flex min-w-0 flex-1 items-center gap-1 sm:gap-2">
            <button
              type="button"
              onClick={() => onSelect(s.step)}
              aria-current={isActive ? 'step' : undefined}
              className={[
                'flex min-w-0 flex-1 items-center gap-2 rounded-xl border px-2.5 py-2 text-left transition-all',
                isActive
                  ? 'border-[#075f55]/40 bg-[#075f55]/5 shadow-sm'
                  : isDone
                    ? 'border-emerald-200 bg-emerald-50/70'
                    : 'border-slate-200 bg-white hover:bg-slate-50',
              ].join(' ')}
            >
              <span
                className={[
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                  isActive ? 'bg-[#075f55] text-white' : isDone ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500',
                ].join(' ')}
              >
                {isDone ? <Check className="h-3.5 w-3.5" /> : s.step}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-slate-900">{s.title}</span>
                <span className="hidden truncate text-[11px] text-slate-500 sm:block">{s.subtitle}</span>
              </span>
            </button>
            {idx < steps.length - 1 && (
              <span aria-hidden className={['hidden h-0.5 w-4 shrink-0 rounded sm:block', isDone ? 'bg-emerald-400' : 'bg-slate-200'].join(' ')} />
            )}
          </div>
        )
      })}
    </nav>
  )
}

interface OrderWizardSummaryBarProps {
  customerName: string
  customerPhone: string
  itemCount: number
  totalLabel: string
  fromConversation: boolean
  channelLabel?: string | null
}

/** Franja de contexto: cliente, canal, nº de productos y total siempre visibles. */
export function OrderWizardSummaryBar({
  customerName,
  customerPhone,
  itemCount,
  totalLabel,
  fromConversation,
  channelLabel,
}: OrderWizardSummaryBarProps) {
  return (
    <div
      role="status"
      aria-label="Resumen de la orden en curso"
      className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-100 bg-gradient-to-r from-emerald-50 via-white to-teal-50 px-4 py-3"
    >
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#075f55] text-white">
          {fromConversation ? <MessageCircle className="h-4 w-4" /> : <User className="h-4 w-4" />}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-slate-900">{customerName || 'Cliente sin definir'}</p>
          <p className="truncate text-xs text-slate-500">
            {[customerPhone, channelLabel, fromConversation ? 'Desde chat' : null].filter(Boolean).join(' · ') || 'Completa los datos del cliente'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4 text-right">
        <div className="flex items-center gap-1.5 text-xs text-slate-600">
          <Package className="h-3.5 w-3.5 text-emerald-700" />
          <span>
            {itemCount} {itemCount === 1 ? 'producto' : 'productos'}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <ShoppingCart className="h-4 w-4 text-emerald-700" />
          <span className="text-base font-black text-emerald-950">{totalLabel}</span>
        </div>
      </div>
    </div>
  )
}
